import { useState, type ReactNode } from 'react'
import { FlaskConical, Info } from 'lucide-react'
import { StoriesShell } from './MainModeStories'

const F = 'Roboto, sans-serif'

interface Props {
  /** 1-based day number within the test week (1–7) */
  testWeekDay: number
}

// #340: карточки «что такое тестовая неделя» — тот же контент, что был в
// раскрытом состоянии плашки, но в формате полноэкранных сторисов (каркас
// StoriesShell — как у сторисов основного режима).
const TEST_WEEK_STEPS: Array<() => ReactNode> = [
  () => (
    <>
      <div style={{ fontSize: 44, textAlign: 'center', marginBottom: 14 }}>🧪</div>
      <h1 style={{ fontSize: 23, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 8px' }}>
        Что такое тестовая неделя?
      </h1>
      <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.75)', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.5 }}>
        Неделя знакомства с приложением — всё работает по-настоящему, кроме очков.
      </p>
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 16, padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 9 }}>
        {[
          { icon: '✅', text: 'Весь функционал приложения доступен в полном объёме' },
          { icon: '✅', text: 'Тренировки, дневник, лекции, дневник питания — всё работает' },
          { icon: '✅', text: 'Куратор видит твои отчёты и может давать обратную связь' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 15, flexShrink: 0, lineHeight: 1.4 }}>{item.icon}</span>
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45 }}>{item.text}</span>
          </div>
        ))}
      </div>
    </>
  ),
  () => (
    <>
      <div style={{ fontSize: 44, textAlign: 'center', marginBottom: 14 }}>🚀</div>
      <h1 style={{ fontSize: 23, fontWeight: 800, color: '#fff', textAlign: 'center', margin: '0 0 8px' }}>
        Очки пока на паузе
      </h1>
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 16, padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 16 }}>
        {[
          { icon: '🚫', text: 'XP и уровни персонажа не изменяются' },
          { icon: '🚫', text: 'TuCoin за выполненные задания не начисляются' },
          { icon: '🚫', text: 'Результаты не учитываются в рейтинге' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 15, flexShrink: 0, lineHeight: 1.4 }}>{item.icon}</span>
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45 }}>{item.text}</span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 14, color: '#FFD166', textAlign: 'center', fontWeight: 700, margin: 0, lineHeight: 1.55 }}>
        После тестовой недели начнётся официальный старт программы — с этого момента
        все очки, TuCoin и рейтинг будут считаться по-настоящему.
      </p>
    </>
  ),
]

export function TestWeekBanner({ testWeekDay }: Props) {
  // #340: тап по плашке открывает карточки-объяснение (раньше — аккордеон).
  const [showStories, setShowStories] = useState(false)
  const day = Math.max(1, Math.min(7, testWeekDay))
  const pct = Math.round((day / 7) * 100)

  return (
    <div
      data-testid="home-test-week-banner"
      style={{
        borderRadius: 18,
        background:
          'linear-gradient(135deg, rgba(255,149,0,0.10) 0%, rgba(255,204,0,0.07) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,149,0,0.28)',
        boxShadow:
          '0 4px 20px rgba(255,149,0,0.14), 0 1px 4px rgba(255,149,0,0.08)',
        overflow: 'hidden',
        fontFamily: F,
        marginBottom: 10,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: -30,
          top: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'rgba(255,149,0,0.10)',
          filter: 'blur(30px)',
          pointerEvents: 'none',
        }}
      />

      <div
        data-testid="test-week-banner-open-stories"
        onClick={() => setShowStories(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 14px',
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 11,
            background: 'rgba(255,149,0,0.16)',
            border: '1px solid rgba(255,149,0,0.30)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FlaskConical size={17} color="#FF9500" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#B85C00',
                lineHeight: 1.2,
              }}
            >
              Тестовая неделя
            </span>
            <span
              data-testid="home-test-week-day-pill"
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: '#FF9500',
                background: 'rgba(255,149,0,0.14)',
                border: '1px solid rgba(255,149,0,0.28)',
                borderRadius: 6,
                padding: '1px 6px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              День {day} / 7
            </span>
          </div>
          <span
            style={{
              fontSize: 11,
              color: '#9A6800',
              display: 'block',
              marginTop: 1,
              lineHeight: 1.35,
            }}
          >
            Очки и TuCoin <b>не начисляются</b>
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <svg width={30} height={30} viewBox="0 0 30 30">
            <circle cx="15" cy="15" r="12" fill="none" stroke="rgba(255,149,0,0.15)" strokeWidth="3" />
            <circle
              cx="15"
              cy="15"
              r="12"
              fill="none"
              stroke="#FF9500"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 12}`}
              strokeDashoffset={`${2 * Math.PI * 12 * (1 - pct / 100)}`}
              transform="rotate(-90 15 15)"
            />
            <text x="15" y="19" textAnchor="middle" fontSize="8" fontWeight="700" fill="#B85C00">{day}</text>
          </svg>
          <Info size={15} color="#B85C00" />
        </div>
      </div>

      <div
        style={{
          height: 3,
          margin: '0 14px',
          borderRadius: 2,
          background: 'rgba(255,149,0,0.12)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #FF9500, #FFCC00)',
            borderRadius: 2,
            transition: 'width 0.4s ease',
          }}
        />
      </div>

      {showStories && (
        <StoriesShell
          steps={TEST_WEEK_STEPS}
          onClose={() => setShowStories(false)}
          finalLabel="Понятно!"
        />
      )}
    </div>
  )
}
